import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Grid, TextField, CircularProgress, Alert } from '@mui/material';

import SimpleDialog, { propTypes } from '@components/Dialog/simple-dialog';

import { useChangePasswordMutation } from '@store/api/user.api.graphql';

// Define the validation schema
const schema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  password: Yup.string().required('New password is required').min(8, 'Password must be at least 8 characters long'),
  passwordConfirmation: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm new password is required'),
});

const ChangePasswordDialog = ({ onClose, ...props }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', message: '' });

  const onSubmit = async data => {
    const { currentPassword, password, passwordConfirmation } = data;
    try {
      await changePassword({ currentPassword, password, passwordConfirmation }).unwrap();
      setFeedbackMessage({ type: 'success', message: 'Password changed successfully.' });
      reset();
    } catch (error) {
      setFeedbackMessage({
        type: 'error',
        message: `Failed to change password. Check the form data and try again.`,
      });
    }
  };

  return (
    <SimpleDialog
      title={'Change Password'}
      {...props}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitButtonTitle={isLoading ? <CircularProgress size={24} /> : 'Change'}
      allowSubmit={!isSubmitting && !isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {feedbackMessage.message && (
            <Grid item xs={12}>
              <Alert severity={feedbackMessage.type}>{feedbackMessage.message}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              {...register('currentPassword')}
              label={'Current password'}
              type={'password'}
              variant="standard"
              fullWidth
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register('password')}
              label="New password"
              type="password"
              variant="standard"
              fullWidth
              error={!!errors.password}
              helperText={errors.password?.message || 'Password must be at least 8 characters long'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register('passwordConfirmation')}
              label="Confirm new password"
              type="password"
              variant="standard"
              size="large"
              fullWidth
              error={!!errors.passwordConfirmation}
              helperText={errors.passwordConfirmation?.message}
            />
          </Grid>
        </Grid>
      </form>
    </SimpleDialog>
  );
};

ChangePasswordDialog.propTypes = propTypes;

export default ChangePasswordDialog;
